import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Empty, Skeleton } from 'antd';
import ColumnHeader from './ColumnHeader';
import AlertCard from './AlertCard';
import { WorkbenchAlert, WorkbenchAlerts } from '../types';
import useHandledAlertPaging from '../hooks/useHandledAlertPaging';

interface AlertsSectionProps {
  data: WorkbenchAlerts;
  warehouseId?: number;
  timeZone?: string | null;
  loading?: boolean;
  onHandled?: () => void;
}

/**
 * 卡片按告警类型排序：领取率在妥投率之前。领取率反映「货还压在仓里」，
 * 是仓管当下能干预的；妥投率是已出仓后的配送进度。上游不保证顺序，故在展示层排。
 * 未收录的类型排在其后，保持上游原顺序（Array.sort 稳定）。
 */
const TYPE_ORDER = ['PICKUP_RATE', 'DELIVERY_RATE'];

const sortByType = (alerts: WorkbenchAlert[]): WorkbenchAlert[] => {
  const rank = (code?: string | null) => {
    const index = code ? TYPE_ORDER.indexOf(code) : -1;
    return index === -1 ? TYPE_ORDER.length : index;
  };
  return [...alerts].sort((a, b) => rank(a.alertTypeCode) - rank(b.alertTypeCode));
};

const AlertsSection: React.FC<AlertsSectionProps> = ({ data, warehouseId, timeZone, loading, onHandled }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('live');
  const paging = useHandledAlertPaging(warehouseId, 'LIVE');

  // 处置成功后除了重拉 board（tab 上的计数），还要作废已缓存的「已处理」分页，
  // 否则那份列表只在首次切 tab 时拉过一次，新处理的告警要整页刷新才看得到
  const { reset } = paging;
  const handleHandled = useCallback(() => {
    reset();
    onHandled?.();
  }, [reset, onHandled]);

  if (loading) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  const liveAlerts = data.alerts.filter((a) => a.handleStatus === 'OPEN');
  const handledAlerts = paging.items ?? data.alerts.filter((a) => a.handleStatus !== 'OPEN');
  const isLive = activeTab === 'live';
  const visible = sortByType(isLive ? liveAlerts : handledAlerts);

  return (
    <div className="border border-red-200 rounded-lg flex flex-col">
      <ColumnHeader
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          if (key === 'handled') paging.loadFirstPageOnce();
        }}
        tabs={[
          { key: 'live', label: String(t('workbench.liveAlerts')), count: liveAlerts.length },
          { key: 'handled', label: String(t('workbench.handledAlerts')), count: data.handledCount },
        ]}
        extra={String(t('workbench.refreshHint'))}
        tone="red"
      />

      {/* min-w-min：overflow-y-auto 会把 overflow-x 一并算成 auto，自动最小尺寸归零，
          告警行的宽度需求就传不到外层 grid。显式声明最小宽度绕开，同时保住 70vh 纵向限高 */}
      <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto min-w-min">
        {visible.length > 0 ? (
          <>
            {visible.map((alert) => (
              <AlertCard
                key={alert.alertId}
                alert={alert}
                warehouseId={warehouseId}
                timeZone={timeZone}
                isHandled={!isLive}
                onHandled={handleHandled}
              />
            ))}
            {!isLive && paging.hasMore ? (
              <div className="text-center">
                <Button type="link" loading={paging.loading} onClick={paging.loadNextPage}>
                  {String(t('workbench.loadMore'))}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-gray-400 text-center pt-2">
                {String(t(isLive ? 'workbench.noMoreLiveAlerts' : 'workbench.noMoreHandledAlerts'))}
              </p>
            )}
          </>
        ) : (
          <Empty
            description={String(t(isLive ? 'workbench.noAlerts' : 'workbench.noHandledAlerts'))}
          />
        )}
      </div>
    </div>
  );
};

export default AlertsSection;
